import React, { useContext, useMemo } from 'react';
import { Breadcrumb } from 'antd';
import { Link, matchPath, useLocation } from 'react-router-dom';

// If you use styled wrappers, keep them; otherwise remove these lines.
import * as S from './DynamicBreadcrumb.styles';
import { BaseRow } from '@app/components/common/BaseRow/BaseRow';
import { BaseCol } from '@app/components/common/BaseCol/BaseCol';
import { VariantContext } from '@app/variant';


export type VariantId = 'control' | 'user';

export interface RouteConfig {
  path?: string;
  index?: boolean;
  element?: React.ReactNode;
  breadcrumb?: string;
  children?: RouteConfig[];
  meta?: {
    /** If omitted => route is visible in both variants */
    variants?: VariantId[];
    /** If true => route exists but is hidden in breadcrumb */
    hideInBreadcrumb?: boolean;
  };
}

interface BreadcrumbsProps {
  routes: RouteConfig[];
}

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ routes }) => {
  const location = useLocation();
  const { id: variantId } = useContext(VariantContext); // 'control' | 'user'

  // -------- helpers --------

  const filterRoutesForVariant = (nodes: RouteConfig[]): RouteConfig[] => {
    const out: RouteConfig[] = [];
    for (const r of nodes) {
      const allowed = !r.meta?.variants || r.meta.variants.includes(variantId);
      if (!allowed) continue;
      const children = r.children ? filterRoutesForVariant(r.children) : undefined;
      out.push({ ...r, children });
    }
    return out;
  };

  /** Build the deepest breadcrumb chain that matches the current pathname */
  const buildBreadcrumbs = (
    nodes: RouteConfig[],
    pathname: string,
    parentPath = ''
  ): { breadcrumb: string; path: string }[] => {
    let best: { breadcrumb: string; path: string }[] = [];

    for (const r of nodes) {
      const fullPath = (r.index ? (parentPath || '/') : `${parentPath}/${r.path ?? ''}`)
        .replace(/\/+/g, '/');

      const matches = matchPath({ path: fullPath, end: false }, pathname);
      if (matches) {
        const here =
          !r.meta?.hideInBreadcrumb && r.breadcrumb
            ? [{ breadcrumb: r.breadcrumb, path: fullPath }]
            : [];

        const childChain = r.children
          ? buildBreadcrumbs(r.children, pathname, fullPath)
          : [];

        const chain = [...here, ...childChain];
        if (chain.length > best.length) best = chain;
      }
    }
    return best;
  };

  // -------- compute --------

  const filteredRoutes = useMemo(
    () => filterRoutesForVariant(routes),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [routes, variantId]
  );

  const breadcrumbs = useMemo(
    () => buildBreadcrumbs(filteredRoutes, location.pathname),
    [filteredRoutes, location.pathname]
  );

  // -------- render --------

  return (
    <BaseRow align="middle">
      <BaseCol>
        <Breadcrumb>
          {breadcrumbs.map((crumb, idx) => {
            const isLast = idx === breadcrumbs.length - 1;
            return (
              <S.BreadcrumbItem key={crumb.path}>
                {isLast ? (
                  <span>{crumb.breadcrumb}</span>
                ) : (
                  <Link to={crumb.path}>{crumb.breadcrumb}</Link>
                )}
              </S.BreadcrumbItem>
            );
          })}
        </Breadcrumb>
      </BaseCol>
    </BaseRow>
  );
};

export default Breadcrumbs;
